/** Read OpenAI SSE across arbitrary network chunk boundaries. */
export async function readChatStream(response: Response, onText: (text: string) => void): Promise<{text: string; finishReason: string | null; artifactFormat: 'docx' | 'xlsx' | 'txt' | null}> {
  if (!response.ok) {
    let message = `Chat request failed (${response.status}).`;
    try {
      const body = await response.json();
      message = typeof body.error === 'string' ? body.error : body.error?.message || message;
    } catch { /* Preserve the HTTP error if the proxy returned HTML. */ }
    throw new Error(message);
  }
  const reader = response.body?.getReader();
  if (!reader) throw new Error('The server returned no response.');
  const decoder = new TextDecoder();
  let buffer = '', text = '', finishReason: string | null = null, completed = false;
  let artifactFormat: 'docx' | 'xlsx' | 'txt' | null = null;
  const consume = (event: string) => {
    const value = event.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart()).join('\n').trim();
    if (!value) return;
    if (value === '[DONE]') { completed = true; return; }
    let data;
    try { data = JSON.parse(value); } catch { throw new Error('The server returned an invalid chat response.'); }
    if (data.error) throw new Error(typeof data.error === 'string' ? data.error : data.error.message || 'The GPU model could not answer.');
    if (['docx', 'xlsx', 'txt'].includes(data.rtc?.artifact_format)) artifactFormat = data.rtc.artifact_format;
    const choice = data.choices?.[0];
    if (typeof choice?.delta?.content === 'string') { text += choice.delta.content; onText(text); }
    if (choice?.finish_reason) finishReason = choice.finish_reason;
  };
  try {
    while (!completed) {
      const {done, value} = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, {stream: true});
      buffer = buffer.replace(/\r\n/g, '\n');
      let boundary;
      while ((boundary = buffer.indexOf('\n\n')) >= 0) {
        consume(buffer.slice(0, boundary)); buffer = buffer.slice(boundary + 2);
        if (completed) break;
      }
      if (done) { if (buffer.trim() && !completed) consume(buffer); break; }
    }
    if (!completed && !finishReason) throw new Error('The GPU connection ended before the answer finished. Please retry.');
    if (!text.trim()) throw new Error('The GPU model returned an empty answer. Please retry.');
    return {text, finishReason, artifactFormat};
  } finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
}
